import React from 'react';
import { ArrowLeft, Lock, Shield, CheckCircle, Server, Key, Users, Eye, Bot, Activity, Webhook, Database } from 'lucide-react';

const TrustSecurity = ({ onBack }) => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center space-x-3">
            <Lock className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Trust & Security</h1>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-8">
            <p className="text-green-800">
              <strong>Security First:</strong> We prioritize the privacy and security of your data using industry-leading practices, zero-retention design, and a transparent privacy model. Our agentic AI platform includes advanced security measures for agent management, self-healing, and analytics.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Shield className="w-6 h-6 mr-2 text-blue-600" />
              Our Security Commitment
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  No Content Storage by Design
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>We do not store any uploaded documents or generated content</li>
                  <li>All content is processed in memory only</li>
                  <li>Files are discarded immediately after the AI generation completes</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  End-to-End Encryption
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>All communication is encrypted with HTTPS/TLS 1.2+</li>
                  <li>API requests, uploads, and downloads are securely transmitted</li>
                  <li>Environment variables and credentials are encrypted at rest on our servers</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Bot className="w-6 h-6 mr-2 text-blue-600" />
              AI Agent Security
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Agent Data Protection</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Agent configurations and prompts are stored with encryption at rest</li>
                    <li>All agent data is isolated by customer and user accounts</li>
                    <li>API keys are securely generated and stored with proper access controls</li>
                    <li>Agent performance data is anonymized for analytics</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Self-Healing Security</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Self-healing mechanisms only suggest changes, never auto-apply without approval</li>
                    <li>All prompt revisions are logged with timestamps and reasoning</li>
                    <li>Performance monitoring uses aggregated, anonymized data</li>
                    <li>Webhook notifications contain only non-sensitive information</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Server className="w-6 h-6 mr-2 text-blue-600" />
              Infrastructure Security
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Hosting & Deployment</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Frontend hosted on Vercel, using global edge CDN and HTTPS by default</li>
                    <li>Backend/API hosted on Railway, using containerized isolated environments</li>
                    <li>Regular patches and infrastructure updates</li>
                    <li>Built-in DDoS protection, request throttling, and scaling safeguards</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Data Protection</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>No persistent storage of any user documents or output</li>
                    <li>Temporary files are stored locally during processing and auto-deleted</li>
                    <li>Secure session handling with expiration and logout enforcement</li>
                    <li>Application-level security reviews and dependency scanning</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Key className="w-6 h-6 mr-2 text-blue-600" />
              Authentication & Access Control
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">User Authentication</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Passwords hashed using bcrypt or equivalent secure algorithms</li>
                    <li>Email verification required for all new accounts</li>
                    <li>Secure password reset via expiring token system</li>
                    <li>Authenticated sessions managed via secure cookies</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Access Controls</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Role-based access control for internal tools and admin panels</li>
                    <li>All API endpoints secured with token-based authentication</li>
                    <li>Rate limiting and abuse prevention applied</li>
                    <li>Session inactivity auto-logout and account lockout mechanisms</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Webhook className="w-6 h-6 mr-2 text-blue-600" />
              Webhook Security
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Webhook Configuration</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Webhook URLs are stored securely and encrypted at rest</li>
                    <li>HTTPS endpoints are required for secure data transmission</li>
                    <li>Webhook payloads contain only non-sensitive information</li>
                    <li>Failed deliveries are retried with exponential backoff</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Delivery Security</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Webhook delivery logs are retained for 30 days for debugging</li>
                    <li>Timeout protection prevents hanging connections</li>
                    <li>Rate limiting prevents webhook spam</li>
                    <li>Webhooks can be disabled at any time through the dashboard</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Activity className="w-6 h-6 mr-2 text-blue-600" />
              Analytics & Monitoring Security
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Performance Analytics</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Agent performance data is aggregated and anonymized</li>
                    <li>Quality scores are stored without personal information</li>
                    <li>Analytics data is isolated by customer and user accounts</li>
                    <li>Trend analysis uses only statistical aggregates</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Feedback Security</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Feedback logs are stored with minimal personal information</li>
                    <li>Quality evaluations are performed securely via API</li>
                    <li>Feedback data is retained for 1 year for quality improvement</li>
                    <li>CSV exports are generated securely with proper access controls</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Users className="w-6 h-6 mr-2 text-blue-600" />
              Privacy & Compliance
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Data Privacy</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>No tracking or storage of user-uploaded content</li>
                    <li>Minimal usage analytics (never tied to uploaded content)</li>
                    <li>Users can delete their accounts and associated data at any time</li>
                    <li>Transparent, human-readable Privacy Policy and Terms of Service</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Compliance</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Follows principles of GDPR compliance, including:</li>
                    <li className="ml-4">Right to access and delete personal data</li>
                    <li className="ml-4">Data processing transparency</li>
                    <li className="ml-4">No international transfer of personal data outside secure providers</li>
                    <li>Planning for SOC 2 compliance as we grow</li>
                    <li>Vendor agreements in place for data handling</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Eye className="w-6 h-6 mr-2 text-blue-600" />
              Transparency & Monitoring
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Security Monitoring</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Continuous monitoring for unusual behavior and system performance</li>
                <li>Regular automated vulnerability scans and dependency checks</li>
                <li>Scheduled third-party security audits and pentests (in progress/planned)</li>
                <li>Real-time monitoring of agent performance and self-healing events</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Incident Response</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Documented incident response plan with defined SLAs</li>
                <li>24/7 alerting and logging of critical infrastructure events</li>
                <li>Automated alerts for agent performance issues and security events</li>
                <li>Webhook notifications for real-time security status updates</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Security</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">AI Providers</h3>
              <p className="text-gray-700 mb-4">
                We use OpenAI's API for content generation and Anthropic's Claude API for agent evaluation and self-healing.
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>OpenAI does not use your data for training when accessed via API</li>
                <li>Anthropic's Claude API deletes all prompts and data after each request</li>
                <li>Both providers comply with SOC 2 and ISO/IEC 27001 standards</li>
                <li>All AI provider communications are encrypted and secure</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Hosting Vendors</h3>
              <p className="text-gray-700 mb-4">
                Vercel and Railway are secure-by-default cloud providers.
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>All vendors undergo regular internal security reviews</li>
                <li>Minimal third-party integrations, with no content shared externally</li>
                <li>All data is encrypted in transit and at rest</li>
                <li>Containerized deployments provide additional isolation</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Security Best Practices for Users</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Choose strong, unique passwords for your account</li>
                <li>Use secure email providers and enable MFA (where available)</li>
                <li>Monitor your feature usage regularly</li>
                <li>Review agent performance analytics and feedback regularly</li>
                <li>Configure webhooks only to secure HTTPS endpoints</li>
                <li>Report suspicious activity to our security team immediately</li>
                <li>Keep your API keys secure and rotate them periodically</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Security Culture</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Ongoing security training and awareness for team members</li>
                <li>Security-first engineering principles in all product decisions</li>
                <li>Rapid patching of vulnerabilities and continuous improvement</li>
                <li>Regular security reviews of agent management and self-healing features</li>
                <li>Transparent communication about security practices and incidents</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Our Security Team</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                If you have questions, concerns, or would like to report a security issue, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-blue-600 font-medium">
                  security@weenable.ai
                </p>
                <p className="text-gray-600 text-sm">
                  We appreciate responsible disclosure and commit to responding quickly.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TrustSecurity; 